import React, { useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, Zap, Target, Layers, Calendar, Download, Monitor } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { productContent as content } from '../content/marketingPages';

/* ── Fade-in observer hook ─────────────────────────────── */
const useFadeIn = () => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { el.classList.add('prod-fade-visible'); observer.unobserve(el); } },
            { threshold: 0.12 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return ref;
};

const FadeIn = ({ children, className = '', delay = 0 }) => {
    const ref = useFadeIn();
    return (
        <div ref={ref} className={`prod-fade-hidden ${className}`} style={{ transitionDelay: `${delay}ms` }}>
            {children}
        </div>
    );
};

/* ── Color tokens ── */
const t = (isDark) => ({
    pageBg:        isDark ? '#171717' : '#ffffff',
    sectionAltBg:  isDark ? '#1c1c1c' : '#fafafa',
    cardBg:        isDark ? '#242424' : '#ffffff',
    cardBorder:    isDark ? '#333'    : '#eee',
    navBg:         isDark ? 'rgba(23,23,23,0.92)' : 'rgba(255,255,255,0.8)',
    navBorder:     isDark ? '#2a2a2a' : '#f5f5f7',
    textPrimary:   isDark ? '#ededed' : '#1d1d1f',
    textSecondary: isDark ? '#999'    : '#666',
    textMuted:     isDark ? '#666'    : '#999',
    footerText:    isDark ? '#555'    : '#ccc',
    accentGlow:    isDark ? 'rgba(255,193,7,0.12)' : 'rgba(255,193,7,0.06)',
});

/* ── Section icons ── */
const sectionIcons = [
    <Zap size={28} className="text-[#FFC107]" />,
    <Target size={28} className="text-[#FFC107]" />,
    <Layers size={28} className="text-[#FFC107]" />,
    <Calendar size={28} className="text-[#FFC107]" />,
    <Download size={28} className="text-[#FFC107]" />,
    <Monitor size={28} className="text-[#FFC107]" />,
];

const ProductDescriptionPage = ({ onBack, onGetStarted }) => {
    const { isDark } = useTheme();
    const c = t(isDark);

    return (
        <div style={{ backgroundColor: c.pageBg, color: c.textPrimary, minHeight: '100vh', fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>

            {/* ── Animation styles ── */}
            <style>{`
                .prod-fade-hidden {
                    opacity: 0;
                    transform: translateY(20px);
                    transition: opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                                transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                }
                .prod-fade-visible {
                    opacity: 1;
                    transform: translateY(0);
                }
                .prod-feature-card {
                    transition: transform 0.25s ease, box-shadow 0.25s ease;
                }
                .prod-feature-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 40px rgba(0,0,0,0.08);
                }
            `}</style>

            {/* ── Nav ── */}
            <nav
                style={{
                    position: 'sticky', top: 0, zIndex: 50,
                    backgroundColor: c.navBg, borderBottom: `1px solid ${c.navBorder}`,
                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                }}
            >
                <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                        onClick={onBack}
                        id="product-back-btn"
                        style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontSize: 14, color: c.textSecondary, background: 'none', border: 'none', cursor: 'pointer',
                            transition: 'color 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = c.textPrimary}
                        onMouseLeave={e => e.currentTarget.style.color = c.textSecondary}
                    >
                        <ArrowLeft size={16} />
                        Back
                    </button>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Zap size={18} className="text-[#FFC107]" />
                        <span style={{ fontWeight: 600, fontSize: 14, color: c.textPrimary }}>Product</span>
                    </div>
                </div>
            </nav>

            {/* ═══════════════════════════════════════════ */}
            {/* ── Hero Section ── */}
            {/* ═══════════════════════════════════════════ */}
            <header style={{ padding: '120px 24px 60px', textAlign: 'center' }}>
                <FadeIn>
                    <h1 style={{
                        fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                        lineHeight: 1.1,
                        marginBottom: 20,
                        color: c.textPrimary,
                    }}>
                        {content.hero.headline}
                    </h1>
                </FadeIn>
                <FadeIn delay={150}>
                    <p style={{
                        fontSize: 'clamp(1.05rem, 2vw, 1.3rem)',
                        lineHeight: 1.65,
                        color: c.textSecondary,
                        maxWidth: 580,
                        margin: '0 auto',
                    }}>
                        {content.hero.subheadline}
                    </p>
                </FadeIn>

                {/* ── Quick stats row ── */}
                <FadeIn delay={300}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, marginTop: 48 }}>
                        {[
                            { value: '60s', label: 'Per lesson' },
                            { value: 'TEKS', label: 'Aligned' },
                            { value: '5 days', label: 'In one click' },
                        ].map(stat => (
                            <div
                                key={stat.label}
                                style={{
                                    backgroundColor: c.cardBg,
                                    border: `1px solid ${c.cardBorder}`,
                                    borderRadius: 12,
                                    padding: '16px 28px',
                                    textAlign: 'center',
                                }}
                            >
                                <p style={{ fontSize: 20, fontWeight: 700, color: c.textPrimary, margin: '0 0 2px' }}>{stat.value}</p>
                                <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.textMuted, margin: 0 }}>{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </FadeIn>
            </header>

            {/* ═══════════════════════════════════════════ */}
            {/* ── Proof Section ── */}
            {/* ═══════════════════════════════════════════ */}
            <section style={{ backgroundColor: c.sectionAltBg, padding: '80px 24px' }}>
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    <FadeIn>
                        <h2 style={{
                            fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                            textAlign: 'center',
                            marginBottom: 56,
                            color: c.textPrimary,
                        }}>
                            From prompt to ready-to-teach
                        </h2>
                    </FadeIn>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        {content.proofSection.map((step, i) => (
                            <FadeIn key={i} delay={i * 100}>
                                <div style={{
                                    display: 'flex', gap: 20, alignItems: 'center',
                                    backgroundColor: c.cardBg,
                                    border: `1px solid ${c.cardBorder}`,
                                    borderRadius: 16,
                                    padding: '28px 32px'
                                }}>
                                    <div style={{
                                        minWidth: 48, height: 48,
                                        borderRadius: '50%',
                                        backgroundColor: '#FFC107',
                                        color: '#1d1d1f',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '1.2rem', fontWeight: 800
                                    }}>
                                        {i + 1}
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: c.textPrimary, marginBottom: 8 }}>{step.title}</h3>
                                        <p style={{ fontSize: '1rem', lineHeight: 1.6, color: c.textSecondary, margin: 0 }}>{step.description}</p>
                                    </div>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════ */}
            {/* ── Feature Sections ── */}
            {/* ═══════════════════════════════════════════ */}
            <section style={{ padding: '80px 24px 80px' }}>
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    <FadeIn>
                        <h2 style={{
                            fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                            textAlign: 'center',
                            marginBottom: 56,
                            color: c.textPrimary,
                        }}>
                            Everything you need to plan with confidence
                        </h2>
                    </FadeIn>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                        gap: 24,
                    }}>
                        {content.sections.map((section, i) => (
                            <FadeIn key={i} delay={i * 80}>
                                <div
                                    className="prod-feature-card"
                                    style={{
                                        backgroundColor: c.cardBg,
                                        border: `1px solid ${c.cardBorder}`,
                                        borderRadius: 16,
                                        padding: '32px 28px',
                                        height: '100%',
                                    }}
                                >
                                    <div style={{
                                        width: 48, height: 48,
                                        borderRadius: 12,
                                        backgroundColor: c.accentGlow,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        marginBottom: 16,
                                    }}>
                                        {sectionIcons[i % sectionIcons.length]}
                                    </div>
                                    <h3 style={{
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        color: c.textPrimary,
                                        marginBottom: 8,
                                    }}>
                                        {section.heading}
                                    </h3>
                                    <p style={{
                                        fontSize: '0.95rem',
                                        lineHeight: 1.7,
                                        color: c.textSecondary,
                                        margin: 0,
                                    }}>
                                        {section.body}
                                    </p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════ */}
            {/* ── CTA Section ── */}
            {/* ═══════════════════════════════════════════ */}
            <section style={{ background: '#FFC107', padding: '100px 24px', textAlign: 'center' }}>
                <FadeIn>
                    <h2 style={{
                        fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        color: '#fff',
                        marginBottom: 16,
                    }}>
                        {content.cta.text}
                    </h2>
                </FadeIn>
                <FadeIn delay={100}>
                    <button
                        onClick={onGetStarted}
                        id="product-cta-btn"
                        style={{
                            background: '#fff',
                            color: '#1d1d1f',
                            padding: '14px 32px',
                            borderRadius: 12,
                            fontSize: 16,
                            fontWeight: 600,
                            border: 'none',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                        {content.cta.button_label}
                        <ArrowRight size={18} />
                    </button>
                </FadeIn>
            </section>

            {/* ── Footer ── */}
            <footer style={{ padding: '48px 24px', textAlign: 'center', fontSize: 12, letterSpacing: '0.04em', color: c.footerText, backgroundColor: c.pageBg }}>
                <p>&copy; 2026 Saige</p>
            </footer>
        </div>
    );
};

export default ProductDescriptionPage;
